/**
 * Утилиты для вычислений в корзине
 */

/**
 * Интерфейс для расчета итоговой суммы
 */
export interface PriceCalculation {
  cartTotal: number           // Исходная сумма корзины (без скидок)
  promocodeDiscount: number   // Скидка по промокоду
  pointsDiscount: number      // Скидка по баллам
  onboardingDiscount?: number // Приветственная скидка (лесенка лояльности)
  giftTierDiscount?: number   // Скидка «подарок за сумму» (цена одной единицы подарка)
  serviceFee: number          // Сервисный сбор
  deliveryCost: number        // Стоимость доставки
  finalAmount: number         // Итоговая сумма к оплате
}

/**
 * Вычисляет сервисный сбор
 * @param baseAmount - базовая сумма для расчета сбора
 * @param serviceFeePercent - процент сервисного сбора
 * @returns сумма сервисного сбора
 */
export function calculateServiceFee(baseAmount: number, serviceFeePercent: number): number {
  if (!serviceFeePercent || serviceFeePercent <= 0) return 0

  const fee = (baseAmount * serviceFeePercent) / 100
  return Math.round(fee)
}

/**
 * Вычисляет итоговую стоимость заказа
 * @param cartTotal - исходная сумма товаров в корзине (БЕЗ скидок)
 * @param promocodeDiscount - скидка по промокоду
 * @param pointsToUse - количество используемых баллов
 * @param deliveryCost - стоимость доставки
 * @param serviceFeePercent - процент сервисного сбора
 * @param giftTierDiscount - скидка «подарок за сумму» (цена одной единицы подарка в корзине)
 * @returns объект с детализацией расчета
 *
 * ⚠️ Формула обязана совпадать с бэкендом (`order-pricing.ts::computeOrderPricing`),
 * иначе фронт покажет одну сумму, а касса спишет другую.
 *
 * Порядок расчёта:
 *   1. totalDiscounts = promocodeDiscount + pointsToUse + giftTierDiscount
 *   2. serviceFee     = round(max(0, cartTotal + deliveryCost − totalDiscounts) × percent / 100)
 *      ↑ доставка включена в базу, т.к. сбор берётся с полной суммы к оплате
 *   3. finalAmount    = max(0, cartTotal + deliveryCost + serviceFee − totalDiscounts)
 *
 * Клампим в ноль ОДИН раз и на полной сумме — как бэкенд. Если клампить товары
 * отдельно от доставки, скидка больше стоимости товаров «сгорает» на фронте, но не
 * на бэке: при товарах 500, доставке 200 и скидке 600 фронт показывал бы 206 ₽,
 * а касса списала бы 103 ₽.
 *
 * Подарок за сумму лежит в корзине по ПОЛНОЙ цене (так его видит касса), поэтому его
 * стоимость вычитается здесь — иначе гость платит за «бесплатное» блюдо и сервисный
 * сбор с него.
 */
export function calculateTotalPrice(
  cartTotal: number,
  promocodeDiscount: number = 0,
  pointsToUse: number = 0,
  deliveryCost: number = 0,
  serviceFeePercent: number = 0,
  giftTierDiscount: number = 0,
): PriceCalculation {
  // 1. Все скидки вычитаются одним пулом (как на бэкенде)
  const totalDiscounts = promocodeDiscount + pointsToUse + giftTierDiscount

  // 2. Сервисный сбор: берётся от суммы после скидок, доставка входит в базу
  //    (ранее доставка игнорировалась — исправлено)
  const baseAmountForServiceFee = Math.max(0, cartTotal + deliveryCost - totalDiscounts)
  const serviceFee = calculateServiceFee(baseAmountForServiceFee, serviceFeePercent)

  // 3. Итоговая сумма к оплате
  const finalAmount = Math.max(0, cartTotal + deliveryCost + serviceFee - totalDiscounts)

  return {
    cartTotal,
    promocodeDiscount,
    pointsDiscount: pointsToUse,
    giftTierDiscount,
    serviceFee,
    deliveryCost,
    finalAmount,
  }
}

/**
 * Форматирует цену для отображения
 * @param amount - сумма
 * @param currency - символ валюты
 * @returns отформатированная строка
 */
export function formatPrice(amount: number | string | null | undefined, currency: string = '₽'): string {
  // Handle different data types safely
  if (amount === null || amount === undefined) {
    return `0 ${currency}`;
  }

  // Convert to number if it's a string
  const numericAmount = typeof amount === 'string' ? Number.parseFloat(amount) : Number(amount);

  // Check if it's a valid number
  if (Number.isNaN(numericAmount)) {
    return `0 ${currency}`;
  }

  return `${Math.round(numericAmount)} ${currency}`;
}

/**
 * Вычисляет процент скидки
 * @param originalAmount - исходная сумма
 * @param discountAmount - сумма скидки
 * @returns процент скидки
 */
export function calculateDiscountPercent(originalAmount: number, discountAmount: number): number {
  if (originalAmount <= 0) return 0
  return Math.round((discountAmount / originalAmount) * 100)
}

/**
 * Проверяет, нужно ли показывать детализацию цен
 * @param calculation - объект расчета
 * @returns true если есть дополнительные сборы или скидки
 */
export function shouldShowPriceBreakdown(calculation: PriceCalculation): boolean {
  return calculation.promocodeDiscount > 0 ||
         calculation.pointsDiscount > 0 ||
         calculation.serviceFee > 0 ||
         calculation.deliveryCost > 0
}

/**
 * Создает объект для отображения детализации цен
 * @param calculation - объект расчета
 * @returns объект для UI
 */
export function createPriceBreakdown(calculation: PriceCalculation) {
  // Подарок лежит в корзине по полной цене, но гость за него не платит — в «Сумме заказа»
  // показываем только ОПЛАЧИВАЕМЫЕ товары. Отдельной строки со скидкой нет специально:
  // иначе подарок вычитался бы дважды и детализация не сходилась бы с «К оплате».
  // Бесплатность видна в блоке «ПОДАРОК» над итогами.
  const payableItemsTotal = Math.max(0, calculation.cartTotal - (calculation.giftTierDiscount ?? 0))

  return {
    cartTotal: {
      label: 'Сумма заказа',
      amount: payableItemsTotal,
      formatted: formatPrice(payableItemsTotal),
    },
    promocodeDiscount: {
      label: 'Скидка по промокоду',
      amount: -calculation.promocodeDiscount,
      formatted: calculation.promocodeDiscount > 0 ? `-${formatPrice(calculation.promocodeDiscount)}` : null,
      show: calculation.promocodeDiscount > 0,
    },
    pointsDiscount: {
      label: 'Списать баллов',
      amount: -calculation.pointsDiscount,
      formatted: calculation.pointsDiscount > 0 ? `-${formatPrice(calculation.pointsDiscount)}` : null,
      show: calculation.pointsDiscount > 0,
    },
    onboardingDiscount: {
      label: 'Скидка на первые заказы',
      amount: -(calculation.onboardingDiscount ?? 0),
      formatted: (calculation.onboardingDiscount ?? 0) > 0 ? `-${formatPrice(calculation.onboardingDiscount)}` : null,
      show: (calculation.onboardingDiscount ?? 0) > 0,
    },
    deliveryCost: {
      label: 'Доставка',
      amount: calculation.deliveryCost,
      formatted: formatPrice(calculation.deliveryCost),
      show: calculation.deliveryCost > 0,
    },
    serviceFee: {
      label: 'Сервисный сбор',
      amount: calculation.serviceFee,
      formatted: formatPrice(calculation.serviceFee),
      show: calculation.serviceFee > 0,
    },
    finalAmount: {
      label: 'К оплате',
      amount: calculation.finalAmount,
      formatted: formatPrice(calculation.finalAmount),
    },
  }
}

/**
 * Валидирует данные для расчета
 * @param cartTotal - сумма корзины
 * @param promocodeDiscount - скидка по промокоду
 * @param pointsToUse - баллы к использованию
 * @param deliveryCost - стоимость доставки
 * @param serviceFeePercent - процент сервисного сбора
 * @returns true если данные валидны
 */
export function validateCalculationData(
  cartTotal: number,
  promocodeDiscount: number,
  pointsToUse: number,
  deliveryCost: number,
  serviceFeePercent: number,
): boolean {
  return cartTotal >= 0 &&
         promocodeDiscount >= 0 &&
         pointsToUse >= 0 &&
         deliveryCost >= 0 &&
         serviceFeePercent >= 0 &&
         serviceFeePercent <= 100
}